import { useAutoAnimate } from "@formkit/auto-animate/react";
import { LockIcon, StackIcon, WarningIcon, WifiIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import React, { ReactElement } from "react";
import { useHistory } from "react-router-dom";
import { Experience, ExperienceStatus } from "types";
import { getDefaultPrice, hasPrivateOption } from "../../models/Experience";
import { BookabilityStatus, useBookabilityStatus } from "../../models/ExperienceBookability";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { sortBy } from "../../utils/locale";
import { Currency } from "../Common/Currency";
import { NoResults } from "../Common/NoResults";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { invalidateArrivalCache } from "../DatePicker/useArrivalTimeslots";

export const ExperienceList = ({ experiences }: { experiences: Experience[] }) => {
    const [animationParent] = useAutoAnimate<HTMLDivElement>();
    const validExperiences = experiences.filter((experience) => experience.status === ExperienceStatus.PUBLISHED);
    const sortedExperiences = sortBy(validExperiences, "name", false);

    return (
        <div className="px-0 py-10">
            <div className="text-lg font-bold">Experiences</div>
            {sortedExperiences.length === 0 ? (
                <NoResults>No results found</NoResults>
            ) : (
                <div
                    ref={animationParent}
                    className="mt-10 grid grid-cols-3 gap-y-5 gap-x-2"
                    data-testid="experience-list"
                >
                    {sortedExperiences.map((experience) => (
                        <ExperienceListItem key={experience.id} experience={experience} />
                    ))}
                </div>
            )}
        </div>
    );
};

const ExperienceListItem = ({ experience }: { experience: Experience }) => {
    const history = useHistory<{ isAddingItem?: boolean } | null>();

    const resetState = useCreatePurchaseStore((state) => state.actions.resetState);

    const initializeExperienceItem = useCreatePurchaseStore((state) => state.actions.initializeExperienceItem);

    const handleExperienceSelect = () => {
        const isAddingItem = history.location.state?.isAddingItem === true;
        const selectedOrganizer = useCreatePurchaseStore.getState().organizer;
        const hasAnyItems = useCreatePurchaseStore.getState().purchase.items.length > 0;

        if (!isAddingItem && (!selectedOrganizer || hasAnyItems)) {
            resetState();
            invalidateArrivalCache(experience.id);
        }

        initializeExperienceItem(experience);

        useCreatePurchaseStore.setState({
            // If user already has any items in the cart, and tries to add a new experience, it would be a MIST checkout.
            // So rather than redirecting him to `CHECKOUT` step, we'll redirect to `SELECT_DATE`
            step: hasAnyItems ? CreatePurchaseStep.SELECT_DATE : CreatePurchaseStep.CHECKOUT,
        });
        history.push("/cart");
    };

    return <ExperienceTile experience={experience} onClick={handleExperienceSelect} />;
};

export const ExperienceTile = ({ experience, onClick }: { experience: Experience; onClick: () => void }) => {
    const price = getDefaultPrice(experience);
    const isVirtual = experience.virtualMeeting?.enabled ?? false;
    const bookabilityStatus = useBookabilityStatus(experience.schedules);

    return (
        <div
            className="flex cursor-pointer flex-row rounded py-2 hover:bg-secondary-hover"
            data-testid="experience-list-item"
            onClick={onClick}
        >
            <ProductImage product={experience} />
            <div className="flex flex-col space-y-1">
                <NoTranslate className="text-md font-bold">{experience.name}</NoTranslate>
                <div className="flex flex-wrap gap-2">
                    {bookabilityStatus !== BookabilityStatus.BOOKABLE ? (
                        <ProductBadge
                            color="danger"
                            text={
                                bookabilityStatus === BookabilityStatus.EXPIRED ? "Schedules Expired" : "Full Blackout"
                            }
                            icon={<WarningIcon />}
                        />
                    ) : (
                        <>
                            <ProductBadge text={getPriceBadgeText(price)} icon={<StackIcon />} />
                            {isVirtual && <ProductBadge color="warning" text="Online Experience" icon={<WifiIcon />} />}
                            {hasPrivateOption(experience) && (
                                <ProductBadge color="warning" text="Private" icon={<LockIcon />} />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const getPriceBadgeText = (price: number) => {
    return (
        <>
            From&nbsp;<Currency>{price}</Currency>
        </>
    );
};

interface ProductBadgeProps {
    color?: string;
    text: string | ReactElement;
    icon: ReactElement;
}

export const ProductBadge = ({ color = "secondary", text, icon }: ProductBadgeProps) => {
    const mediumIcon = React.cloneElement(icon, { size: icon.props.size ?? "medium" });
    return (
        <Badge color={color} size="medium" icon={mediumIcon}>
            <span className="line m-2 ml-1 whitespace-normal break-words">{text}</span>
        </Badge>
    );
};
